import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Plus, Trash2, Upload, Image as ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreatePurchaseDialog({ open, onClose, onSuccess }) {
  const [ingredients, setIngredients] = useState([]);
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemsLoaded, setItemsLoaded] = useState(false);
  const [formData, setFormData] = useState({
    supplier_name: '',
    invoice_number: '',
    purchase_date: new Date().toISOString().split('T')[0],
    branch_id: '',
    items: [],
    shipping_cost: 0,
    other_costs: 0,
    notes: ''
  });
  const [receiptImage, setReceiptImage] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    const loadItems = async () => {
      if (open) {
        setLoadingItems(true);
        setItemsLoaded(false);
        await fetchBranches();
        setItemsLoaded(true);
        setLoadingItems(false);
      }
    };
    
    loadItems();
  }, [open]);

  // Fetch items when branch changes
  useEffect(() => {
    if (open && formData.branch_id) {
      fetchIngredients(formData.branch_id);
      fetchProducts(formData.branch_id);
    }
  }, [open, formData.branch_id]);
  
  useEffect(() => {
    // Show warning only after items are loaded and both are empty
    if (open && itemsLoaded && !loadingItems && ingredients.length === 0 && products.length === 0) {
      toast.warning('Belum ada bahan baku atau produk. Silakan tambahkan terlebih dahulu di menu Bahan Baku atau Produk F&B.');
    }
  }, [open, itemsLoaded, loadingItems, ingredients, products]);

  const fetchIngredients = async (branchId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/ingredients?branch_id=${branchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ingredientsList = response.data.ingredients || [];
      setIngredients(ingredientsList);
      console.log('Ingredients loaded for branch:', branchId, ingredientsList.length);
    } catch (error) {
      console.error('Error fetching ingredients:', error);
      toast.error('Gagal memuat daftar bahan baku');
    }
  };

  const fetchProducts = async (branchId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/products?branch_id=${branchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Only simple products (not composite)
      const simpleProducts = (response.data.products || []).filter(p => p.type !== 'composite');
      setProducts(simpleProducts);
      console.log('Products loaded for branch:', branchId, simpleProducts.length);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Gagal memuat daftar produk');
    }
  };

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const branchesList = response.data.branches || [];
      setBranches(branchesList);
      console.log('Branches loaded:', branchesList.length);
    } catch (error) {
      console.error('Error fetching branches:', error);
      toast.error('Gagal memuat daftar cabang');
    }
  };

  const addItem = () => {
    // Show category selection modal
    setShowCategoryModal(true);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setShowCategoryModal(false);
    
    // Add item with selected category
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          item_id: '',
          item_type: category === 'simple' || category === 'composite' ? 'product' : 'ingredient',
          item_category: category, // 'simple', 'composite', 'ingredient_stock', 'ingredient_nonstock'
          item_name: '',
          is_non_stock: category === 'ingredient_nonstock',
          quantity: '',
          price_per_unit: '',
          purchase_price: '',
          lastEdited: 'total'
        }
      ]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    const item = newItems[index];
    
    // Update the field
    item[field] = value;
    
    // Auto-calculate based on category and field
    const quantity = parseFloat(item.quantity) || 0;
    const category = item.item_category;
    
    if (field === 'quantity') {
      // For all categories: recalculate based on which field exists
      if (item.price_per_unit) {
        item.purchase_price = quantity * parseFloat(item.price_per_unit);
      } else if (item.purchase_price && quantity > 0) {
        item.price_per_unit = parseFloat(item.purchase_price) / quantity;
      }
    } else if (field === 'price_per_unit') {
      // Calculate total from price per unit
      item.purchase_price = quantity * parseFloat(value || 0);
      
      // For non-stock: mark as edited price per unit
      if (category === 'ingredient_nonstock') {
        item.lastEdited = 'pricePerUnit';
      }
    } else if (field === 'purchase_price') {
      // Calculate price per unit from total
      item.price_per_unit = quantity > 0 ? parseFloat(value || 0) / quantity : 0;
      
      // For non-stock: mark as edited total
      if (category === 'ingredient_nonstock') {
        item.lastEdited = 'total';
      }
    } else if (field === 'item_id') {
      // When item is selected, get item info and validate category
      let selectedItem;
      
      if (category === 'simple' || category === 'composite') {
        selectedItem = products.find(prod => prod.id === value);
        if (selectedItem) {
          item.item_name = selectedItem.name;
          item.item_type = 'product';
        }
      } else if (category === 'ingredient_stock' || category === 'ingredient_nonstock') {
        selectedItem = ingredients.find(ing => ing.id === value);
        if (selectedItem) {
          item.item_name = selectedItem.name;
          item.item_type = 'ingredient';
          item.is_non_stock = selectedItem.is_non_stock || false;
          
          // Validate if non-stock category matches ingredient
          if (category === 'ingredient_nonstock' && !selectedItem.is_non_stock) {
            toast.error(`${selectedItem.name} bukan Non-Stock item!`);
            item.item_id = '';
            item.item_name = '';
          } else if (category === 'ingredient_stock' && selectedItem.is_non_stock) {
            toast.error(`${selectedItem.name} adalah Non-Stock item! Pilih kategori Non-Stock.`);
            item.item_id = '';
            item.item_name = '';
          }
        }
      }
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('File harus berupa gambar');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setReceiptImage(reader.result);
      setReceiptPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => {
      // purchase_price is now TOTAL price (not per unit), so just sum them up
      const totalPrice = parseFloat(item.purchase_price) || 0;
      return sum + totalPrice;
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const shipping = parseFloat(formData.shipping_cost) || 0;
    const other = parseFloat(formData.other_costs) || 0;
    return subtotal + shipping + other;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.supplier_name.trim()) {
      toast.error('Nama supplier harus diisi');
      return;
    }

    if (!formData.invoice_number.trim()) {
      toast.error('Nomor nota harus diisi');
      return;
    }

    if (!formData.branch_id) {
      toast.error('Cabang harus dipilih');
      return;
    }

    if (formData.items.length === 0) {
      toast.error('Minimal harus ada 1 item');
      return;
    }

    // Validate items
    for (let i = 0; i < formData.items.length; i++) {
      const item = formData.items[i];
      if (!item.item_id) {
        toast.error(`Item ${i + 1}: Pilih bahan baku atau produk`);
        return;
      }
      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        toast.error(`Item ${i + 1}: Quantity harus lebih dari 0`);
        return;
      }
      if (!item.purchase_price || parseFloat(item.purchase_price) <= 0) {
        toast.error(`Item ${i + 1}: Harga total harus lebih dari 0`);
        return;
      }
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      
      // Prepare data
      const purchaseData = {
        ...formData,
        branch_id: formData.branch_id,
        items: formData.items.map(item => ({
          item_id: item.item_id,
          item_type: item.item_type,
          quantity: parseFloat(item.quantity),
          purchase_price: parseFloat(item.purchase_price)
        })),
        shipping_cost: parseFloat(formData.shipping_cost) || 0,
        other_costs: parseFloat(formData.other_costs) || 0,
        receipt_image: receiptImage
      };

      console.log('Sending purchase data:', purchaseData);

      const response = await axios.post(`${API}/purchases`, purchaseData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Purchase response:', response.data);
      // Don't show toast here - parent component will handle it
      onSuccess();
    } catch (error) {
      console.error('Error creating purchase:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      const errorMessage = error.response?.data?.detail || error.message || 'Gagal membuat pembelian';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (!open) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Buat Pembelian Baru</h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-all"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <h3 className="font-semibold text-gray-800">Informasi Pembelian</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nama Supplier *
                  </label>
                  <input
                    type="text"
                    value={formData.supplier_name}
                    onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nama toko/supplier"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nomor Nota *
                  </label>
                  <input
                    type="text"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="No. nota/invoice"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tanggal Pembelian *
                  </label>
                  <input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cabang *
                  </label>
                  <select
                    value={formData.branch_id}
                    onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Pilih Cabang</option>
                    {branches.map((branch) => (
                      <option key={branch.branch_id} value={branch.branch_id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                     Stok akan ditambahkan ke cabang yang dipilih
                  </p>
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-800">Daftar Item</h3>
                <button
                  type="button"
                  onClick={addItem}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-sm font-semibold"
                >
                  <Plus className="h-4 w-4" />
                  Tambah Item
                </button>
              </div>

              {formData.items.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">Belum ada item. Klik "Tambah Item" untuk mulai.</p>
              ) : (
                <div className="space-y-3">
                  {formData.items.map((item, index) => {
                    // Find the selected item (ingredient or product)
                    const selectedIngredient = ingredients.find(ing => ing.id === item.item_id);
                    const selectedProduct = products.find(prod => prod.id === item.item_id);
                    const selectedItem = selectedIngredient || selectedProduct;
                    const itemUnit = selectedIngredient ? selectedIngredient.unit : 'pcs';
                    
                    // purchase_price is already the TOTAL price for this item
                    const itemSubtotal = parseFloat(item.purchase_price) || 0;

                    // Get category info
                    const categoryInfo = {
                      'simple': { label: 'Produk Simple', icon: '', color: 'blue' },
                      'composite': { label: 'Produk Composite', icon: '', color: 'purple' },
                      'ingredient_stock': { label: 'Bahan Baku (Stock)', icon: '', color: 'green' },
                      'ingredient_nonstock': { label: 'Bahan Baku (Non-Stock)', icon: '', color: 'orange' }
                    };
                    const catInfo = categoryInfo[item.item_category] || { label: 'Unknown', icon: '', color: 'gray' };

                    return (
                      <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                        {/* Category Badge - Full Width */}
                        <div className="mb-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 bg-${catInfo.color}-100 border border-${catInfo.color}-300 rounded-md text-xs font-bold text-${catInfo.color}-700`}>
                            {catInfo.icon} {catInfo.label}
                          </span>
                        </div>

                        <div className="flex items-start gap-3">
                          <div className="flex-1 space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              {/* Item Selection - Filtered by Category */}
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  {item.item_category === 'simple' || item.item_category === 'composite' ? 'Pilih Produk' : 'Pilih Bahan Baku'}
                                </label>
                                <select
                                  value={item.item_id}
                                  onChange={(e) => {
                                    updateItem(index, 'item_id', e.target.value);
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                  required
                                >
                                  <option value="">Pilih item</option>
                                  
                                  {/* Show products for simple/composite */}
                                  {(item.item_category === 'simple' || item.item_category === 'composite') && 
                                    products.map(prod => (
                                      <option key={prod.id} value={prod.id}>
                                        {prod.name} {prod.type === 'composite' ? '(Komposit)' : '(Simple)'}
                                      </option>
                                    ))
                                  }
                                  
                                  {/* Show ingredients for bahan baku */}
                                  {(item.item_category === 'ingredient_stock' || item.item_category === 'ingredient_nonstock') && 
                                    ingredients
                                      .filter(ing => {
                                        // Filter based on category
                                        if (item.item_category === 'ingredient_nonstock') {
                                          return ing.is_non_stock === true;
                                        } else {
                                          return !ing.is_non_stock;
                                        }
                                      })
                                      .map(ing => (
                                        <option key={ing.id} value={ing.id}>
                                          {ing.name} ({ing.unit})
                                        </option>
                                      ))
                                  }
                                </select>
                              </div>

                            {/* Quantity */}
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Quantity {itemUnit && `(${itemUnit})`}
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.quantity}
                                onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                placeholder="0"
                                required
                              />
                            </div>

                            {/* Harga Satuan */}
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Harga Satuan {item.item_category === 'ingredient_nonstock' && ''}
                              </label>
                              <input
                                type="number"
                                step="1"
                                min="0"
                                value={item.price_per_unit || ''}
                                onChange={(e) => updateItem(index, 'price_per_unit', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="Rp per unit"
                              />
                            </div>

                            {/* Total Harga */}
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Total Harga {item.item_category === 'ingredient_nonstock' && ''}
                              </label>
                              <input
                                type="number"
                                step="1"
                                min="0"
                                value={item.purchase_price || ''}
                                onChange={(e) => updateItem(index, 'purchase_price', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="Total harga"
                                required
                              />
                            </div>
                            </div>

                            {/* Info untuk Non-Stock */}
                            {item.item_category === 'ingredient_nonstock' && (
                              <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 mt-2">
                                <p className="text-xs text-orange-700">
                                  <strong> Tip:</strong> Isi salah satu (Harga Satuan atau Total), yang lain otomatis dihitung. 
                                  Pembelian ini masuk <strong>Biaya Operasional</strong>, tidak update stock.
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Delete Button */}
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="mt-6 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Additional Costs */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <h3 className="font-semibold text-gray-800">Biaya Tambahan</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ongkir
                  </label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={formData.shipping_cost}
                    onChange={(e) => setFormData({ ...formData, shipping_cost: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Biaya Lain-lain
                  </label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={formData.other_costs}
                    onChange={(e) => setFormData({ ...formData, other_costs: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            {/* Upload Receipt */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <h3 className="font-semibold text-gray-800">Upload Nota</h3>
              
              <div>
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-all">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Klik untuk upload</span> atau drag & drop
                    </p>
                    <p className="text-xs text-gray-400">PNG, JPG maksimal 5MB</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </label>

                {receiptPreview && (
                  <div className="mt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <ImageIcon className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-green-600">Foto nota berhasil diupload</span>
                    </div>
                    <img
                      src={receiptPreview}
                      alt="Preview"
                      className="max-w-xs rounded-lg border border-gray-300"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <h3 className="font-semibold text-gray-800">Catatan</h3>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="3"
                placeholder="Catatan tambahan (opsional)"
              />
            </div>

            {/* Total Summary */}
            <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal Items:</span>
                  <span className="font-semibold">{formatCurrency(calculateSubtotal())}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Ongkir:</span>
                  <span className="font-semibold">{formatCurrency(parseFloat(formData.shipping_cost) || 0)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Biaya Lain:</span>
                  <span className="font-semibold">{formatCurrency(parseFloat(formData.other_costs) || 0)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t-2 border-blue-300 pt-2">
                  <span className="text-gray-900">Total Pembayaran:</span>
                  <span className="text-blue-600">{formatCurrency(calculateTotal())}</span>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-all font-semibold"
          >
            Batal
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting || formData.items.length === 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Menyimpan...' : 'Simpan Pembelian'}
          </button>
        </div>
      </div>

      {/* Category Selection Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Pilih Kategori Item</h3>
            <p className="text-sm text-gray-600 mb-6">
              Pilih jenis item yang akan dibeli untuk menentukan bagaimana item ini akan diproses dalam sistem
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Produk Simple */}
              <button
                onClick={() => handleCategorySelect('simple')}
                className="group p-4 border-2 border-blue-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-all">
                    <span className="text-2xl"></span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-1">Produk Simple</h4>
                    <p className="text-xs text-gray-600">
                      Produk jadi siap jual (contoh: Teh Sariwangi, Chitato)
                    </p>
                    <div className="mt-2 text-xs text-blue-600 font-semibold">
                       Update Stock  Calculate HPP
                    </div>
                  </div>
                </div>
              </button>

              {/* Produk Composite */}
              <button
                onClick={() => handleCategorySelect('composite')}
                className="group p-4 border-2 border-purple-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-all">
                    <span className="text-2xl"></span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-1">Produk Composite</h4>
                    <p className="text-xs text-gray-600">
                      Produk yang dirakit dari bahan baku (contoh: Nasi Goreng, Mie Goreng)
                    </p>
                    <div className="mt-2 text-xs text-purple-600 font-semibold">
                       Update Stock  Calculate HPP
                    </div>
                  </div>
                </div>
              </button>

              {/* Bahan Baku Stock */}
              <button
                onClick={() => handleCategorySelect('ingredient_stock')}
                className="group p-4 border-2 border-green-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all text-left"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-all">
                    <span className="text-2xl"></span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-1">Bahan Baku (Stock)</h4>
                    <p className="text-xs text-gray-600">
                      Bahan yang perlu tracking stock (contoh: Telur, Mie, Ayam)
                    </p>
                    <div className="mt-2 text-xs text-green-600 font-semibold">
                       Update Stock  Calculate HPP
                    </div>
                  </div>
                </div>
              </button>

              {/* Bahan Baku Non-Stock */}
              <button
                onClick={() => handleCategorySelect('ingredient_nonstock')}
                className="group p-4 border-2 border-orange-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-all text-left"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg group-hover:bg-orange-200 transition-all">
                    <span className="text-2xl"></span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-1">Bahan Non-Stock</h4>
                    <p className="text-xs text-gray-600">
                      Bahan consumable tidak perlu tracking (contoh: Es Batu, Margarin, Bumbu)
                    </p>
                    <div className="mt-2 text-xs text-orange-600 font-semibold">
                       Masuk Biaya Operasional
                    </div>
                  </div>
                </div>
              </button>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowCategoryModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}
